import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import {JdTableSelectOptions} from "./select.interface";
import {Subject} from "rxjs";
import { distinctUntilChanged, takeUntil} from "rxjs/operators";
import {isFunction} from 'lodash';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";

export const JD_TABLE_SELECT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  multi: true,
  useExisting: forwardRef(() => TableSelectComponent)
};

@Component({
  selector: 'jd-table-select',
  templateUrl: './table-select.component.html',
  styleUrls: ['./table-select.component.less'],
  providers: [JD_TABLE_SELECT_VALUE_ACCESSOR],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableSelectComponent<T = any> implements OnInit, OnDestroy, ControlValueAccessor {
  _onChange: Function;
  _onTouched: Function;

  _value: any;

  @Input() jdDisabled: boolean;

  @Input() jdAllowClear = true;

  @Input() jdSize = 'default';

  @Input() jdOptions: JdTableSelectOptions[] = [];

  @Input() jdTableSelectData: T[] = [];

  @Input() jdValueKey: string;

  @Input() overlayMinWidth: number;

  @Input() overlayWidth: number;

  @Input() jdPlaceHolder: string;

  @Input() jdShowSearch: boolean;

  @Input() isLoading: boolean;

  @Input() jdLabelKey: string;

  @Input() jdCustomDisplayLabel: (data: T) => string;

  @Input() jdCustomValue: (data: T) => string;

  @Output() OnSelectedChange = new EventEmitter<T>();

  @Output() OnSearch = new EventEmitter<string>();

  @Output() OnOpenChange = new EventEmitter<boolean>();

  @Output() OnNotSearchResult = new EventEmitter<{isNeedLoading: boolean, value: string}>();

  private _onSelectedChange$ = new Subject<T>();
  private _unsubscribe$ = new Subject();

  _select(data: T) {
    this._onSelectedChange$.next(data);
  }

  _ngModelChange($event) {
    this._value = $event;
    isFunction(this._onChange) && this._onChange($event);
  }

  _onNotSearchResult($event: {isNeedLoading: boolean, value: string}) {
    this.OnNotSearchResult.emit($event);
  }

  _openChange($event: boolean) {
    this.OnOpenChange.emit($event);
  }

  _onSearch($event: string) {
    this.OnSearch.emit($event);
  }

  getLabel(data: T) {
    if (!this.jdLabelKey) {
      this.jdLabelKey = Object.keys(data)[0];
    }
    return (isFunction(this.jdCustomDisplayLabel) && this.jdCustomDisplayLabel(data)) || data[this.jdLabelKey];
  }

  getValue(data: T) {
    if (!this.jdValueKey) {
      this.jdValueKey = Object.keys(data)[0];
    }
    return (isFunction(this.jdCustomValue) && this.jdCustomValue(data)) || data[this.jdValueKey];
  }
  constructor() { }

  ngOnInit() {
    this._onSelectedChange$
      .pipe(
        takeUntil(this._unsubscribe$),
        distinctUntilChanged((x: T, y: T) => JSON.stringify(x) === JSON.stringify(y))
      )
      .subscribe(value => this.OnSelectedChange.emit(value));
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.jdDisabled = isDisabled;
  }

  writeValue(obj: any): void {
    this._value = obj;
  }

}
